import { useState, useCallback } from 'react';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';

interface UseDiarizationCorrectionProps {
  onAcceptCorrection?: (correctionId: string, blockId: string) => void;
  onRejectCorrection?: (correctionId: string, blockId: string) => void;
}

interface UseDiarizationCorrectionReturn {
  isProcessing: boolean;
  openPopoverId: string | null;
  handleAcceptCorrection: (correctionId: string, blockId: string) => void;
  handleRejectCorrection: (correctionId: string, blockId: string) => void;
  handlePopoverOpenChange: (correctionId: string, open: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
}

export const useDiarizationCorrection = ({
  onAcceptCorrection,
  onRejectCorrection,
}: UseDiarizationCorrectionProps): UseDiarizationCorrectionReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const handleAcceptCorrection = useCallback(
    (correctionId: string, blockId: string) => {
      if (onAcceptCorrection) {
        setIsProcessing(true);
        onAcceptCorrection(correctionId, blockId);
        trackCustomEvent(TrackingEventsV2.AcceptDiarizationCorrection);
        // The processing state will be reset when we receive the completion event via WebSocket
      }
    },
    [onAcceptCorrection],
  );

  const handleRejectCorrection = useCallback(
    (correctionId: string, blockId: string) => {
      if (onRejectCorrection) {
        setIsProcessing(true);
        onRejectCorrection(correctionId, blockId);
        trackCustomEvent(TrackingEventsV2.RejectDiarizationCorrection);
        // The processing state will be reset when we receive the completion event via WebSocket
      }
    },
    [onRejectCorrection],
  );

  const handlePopoverOpenChange = useCallback(
    (correctionId: string, open: boolean) => {
      if (open) {
        setOpenPopoverId(correctionId);
      } else {
        setOpenPopoverId(null);
        // Reset processing state when popover closes
        if (isProcessing) {
          setIsProcessing(false);
        }
      }
    },
    [isProcessing],
  );

  return {
    isProcessing,
    openPopoverId,
    handleAcceptCorrection,
    handleRejectCorrection,
    handlePopoverOpenChange,
    setIsProcessing,
  };
};
